import type { FacturaCompraFormularioDto, CreateItemCompraDto } from '../services/facturaCompraService';
import type {
  FacturaCompraPrecargaDesdePdfResult,
  FacturaCompraPrecargaItem,
} from '../services/facturaCompraService';
import type { Proveedor } from '../services/proveedorService';
import type { EmisorFacturaCompraPdf } from './compraFacturaPdfEmisor';
import { formatCuit11 } from './compraFacturaPdfEmisor';
import { formaDePagoDesdeCondVtaProveedor } from './formaPagoCompra';

export const PRECARGA_CONFIANZA_MINIMA = 0.5;

export const letraFromTipoIvaProveedor = (tipoIva: string | undefined): string => {
  const t = (tipoIva ?? '').trim().toUpperCase();
  if (t === 'X') return 'X';
  if (t === 'C' || t === 'CF') return 'C';
  if (t === 'I' || t === 'M') return 'A';
  return t ? 'B' : 'A';
};

export const computeTipoComprobanteCompra = (letraValue: string, esNota: boolean): string => {
  const L = (letraValue || '').trim().toUpperCase();
  const esFactura = !esNota;
  if (L === 'A') return esFactura ? '01' : '02';
  if (L === 'B') return esFactura ? '06' : '07';
  if (L === 'C') return esFactura ? '11' : '12';
  return '00';
};

export interface CompaniaPrecargaRef {
  idCompania: string;
  condIva?: string;
}

export interface ImpuestoTasasPrecargaRef {
  impuestotasa1?: number;
  impuestotasa2?: number;
  impuestotasa3?: number;
}

export interface AplicarPrecargaParams {
  precarga: FacturaCompraPrecargaDesdePdfResult;
  formularioActual: FacturaCompraFormularioDto;
  companias: CompaniaPrecargaRef[];
  formasDePago: readonly string[];
  proveedorDetalle?: Proveedor | null;
  impuestoTasas?: ImpuestoTasasPrecargaRef;
  confianzaMinima?: number;
}

export interface AplicarPrecargaResult {
  formulario: FacturaCompraFormularioDto;
  proveedorNombre: string;
  proveedorCuit: string;
  proveedorTipoIva: string;
  esNotaDebito: boolean;
  expandConceptos: boolean;
  itemsProductos: CreateItemCompraDto[];
  itemsConceptos: CreateItemCompraDto[];
}

function campoConfiable(
  campos: FacturaCompraPrecargaDesdePdfResult['campos'],
  nombre: string,
  min: number
): boolean {
  const c = campos.find((x) => x.nombre === nombre);
  return c != null && c.confianza >= min;
}

export function aplicarPrecargaEncabezado({
  precarga,
  formularioActual,
  companias,
  formasDePago,
  proveedorDetalle,
  impuestoTasas,
  confianzaMinima = PRECARGA_CONFIANZA_MINIMA,
}: AplicarPrecargaParams): AplicarPrecargaResult {
  const f = precarga.formulario ?? {};
  const min = confianzaMinima;
  const campos = precarga.campos ?? [];

  const next: FacturaCompraFormularioDto = { ...formularioActual };

  if (f.letra && campoConfiable(campos, 'letra', min)) {
    next.letra = f.letra.trim().toUpperCase();
  }

  if (f.nroSuc != null && f.nroSuc > 0 && campoConfiable(campos, 'nroSuc', min)) {
    next.nroSuc = f.nroSuc;
  }

  if (f.nroComprobante != null && f.nroComprobante > 0 && campoConfiable(campos, 'nroComprobante', min)) {
    next.nroComprobante = f.nroComprobante;
  }

  if (f.descripcion?.trim()) {
    next.descripcion = f.descripcion.trim();
  }

  if (f.fechaEmision && campoConfiable(campos, 'fechaEmision', min)) {
    next.fechaEmision = f.fechaEmision;
  }

  if (f.fechaVencim && campoConfiable(campos, 'fechaVencim', min)) {
    next.fechaVencim = f.fechaVencim;
  }

  if (f.aplicacionIva) {
    next.aplicacionIva = f.aplicacionIva;
  } else if (f.fechaEmision) {
    next.aplicacionIva = f.fechaEmision;
  }

  let proveedorNombre = precarga.proveedor.nombre?.trim() ?? '';
  let proveedorCuit = precarga.proveedor.cuit?.trim() ?? '';
  let proveedorTipoIva = '';
  let expandConceptos = false;
  const esNotaDebito = precarga.esNotaDebito;

  if (precarga.proveedor.encontrado && precarga.proveedor.nroProveedor) {
    next.nroProveedor = precarga.proveedor.nroProveedor;
    if (!proveedorNombre) {
      proveedorNombre = precarga.proveedor.nombre?.trim() ?? '';
    }

    if (proveedorDetalle) {
      proveedorNombre = proveedorDetalle.nombre?.trim() || proveedorNombre;
      proveedorCuit = proveedorDetalle.cuit?.trim() || proveedorCuit;
      proveedorTipoIva = (proveedorDetalle.tipoIva ?? '').trim().toUpperCase();

      let letraSugerida = next.letra?.trim()
        ? next.letra
        : letraFromTipoIvaProveedor(proveedorDetalle.tipoIva);

      const companiaEmisora = companias.find((c) => c.idCompania === next.idCompania);
      const condIvaNorm = (companiaEmisora?.condIva ?? '')
        .toUpperCase()
        .replace(/\./g, '')
        .replace(/\s+/g, ' ')
        .trim();
      if (condIvaNorm === 'RESP MONOTRIBUTO' || condIvaNorm.includes('MONOTRIBUTO')) {
        letraSugerida = 'C';
      }

      if (!next.letra?.trim() || !campoConfiable(campos, 'letra', min)) {
        next.letra = letraSugerida;
      }

      const tipoProveedorSugerido =
        (proveedorDetalle.tipoProveedor ?? '').trim().toUpperCase() === 'G' ? 'G' : 'C';
      next.tipoProveedor = f.tipoProveedor?.trim() === 'G' ? 'G' : tipoProveedorSugerido;
      expandConceptos = next.tipoProveedor === 'G';

      const fallbackFp =
        next.formaDePago && formasDePago.includes(next.formaDePago) ? next.formaDePago : 'CONTADO';
      next.formaDePago = formaDePagoDesdeCondVtaProveedor(formasDePago, proveedorDetalle, fallbackFp);
    } else if (f.tipoProveedor) {
      next.tipoProveedor = f.tipoProveedor.trim().toUpperCase() === 'G' ? 'G' : 'C';
      expandConceptos = next.tipoProveedor === 'G';
    }
  } else if (f.tipoProveedor) {
    // Sin match en maestro: dejar nombre/CUIT del PDF para completar tras elegir OCASIONAL u otro proveedor.
    next.tipoProveedor = f.tipoProveedor.trim().toUpperCase() === 'G' ? 'G' : 'C';
    expandConceptos = next.tipoProveedor === 'G';
  }

  const letraFinal = next.letra || 'A';
  next.tipoComprobante = computeTipoComprobanteCompra(letraFinal, esNotaDebito);

  return {
    formulario: next,
    proveedorNombre,
    proveedorCuit,
    proveedorTipoIva,
    esNotaDebito,
    expandConceptos,
    itemsProductos: mapPrecargaItems(precarga.itemsProductos, 'C', letraFinal, impuestoTasas),
    itemsConceptos: mapPrecargaItems(precarga.itemsConceptos, 'G', letraFinal, impuestoTasas),
  };
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Emula la carga manual: letra A → IVA en tasa correspondiente; letra C → no gravado (tasa5). */
export function applyTasasPrecargaItem(
  letra: string,
  cantidad: number,
  costoBrutoUnitario: number,
  porcentajeIva?: number | null,
  impuestoTasas?: ImpuestoTasasPrecargaRef,
): Pick<CreateItemCompraDto, 'tasa1' | 'tasa2' | 'tasa3' | 'tasa4' | 'tasa5'> {
  const L = (letra || '').trim().toUpperCase();
  const netoTotal = round2(cantidad * costoBrutoUnitario);
  const zeros = { tasa1: 0, tasa2: 0, tasa3: 0, tasa4: 0, tasa5: 0 };

  if (L === 'C') {
    return netoTotal > 0 ? { ...zeros, tasa5: netoTotal } : zeros;
  }

  if (L !== 'A' || netoTotal <= 0) return zeros;

  const pct = porcentajeIva ?? impuestoTasas?.impuestotasa1 ?? 21;
  const iva = round2(netoTotal * (pct / 100));
  const t1 = impuestoTasas?.impuestotasa1 ?? 21;
  const t2 = impuestoTasas?.impuestotasa2 ?? 10.5;
  const t3 = impuestoTasas?.impuestotasa3 ?? 27;

  if (Math.abs(pct - t2) < 0.01) return { ...zeros, tasa2: iva };
  if (Math.abs(pct - t3) < 0.01) return { ...zeros, tasa3: iva };
  if (Math.abs(pct - t1) < 0.01 || pct > 0) return { ...zeros, tasa1: iva };

  return zeros;
}

const mapPrecargaItems = (
  items: FacturaCompraPrecargaItem[] | undefined,
  tipo: 'C' | 'G',
  letra: string,
  impuestoTasas?: ImpuestoTasasPrecargaRef,
): CreateItemCompraDto[] =>
  (items ?? [])
    .filter((it) => it.cantidad > 0 && (it.codigoArticulo?.trim() || it.descripcion?.trim()))
    .map((it) => {
      const cantidad = it.cantidad;
      const costoPdf = it.costoBrutoUnitario ?? 0;
      const esLetraC = letra.trim().toUpperCase() === 'C';
      const costoBrutoUnitario = esLetraC ? 0 : costoPdf;
      const tasas = applyTasasPrecargaItem(
        letra,
        cantidad,
        esLetraC ? costoPdf : costoBrutoUnitario,
        it.porcentajeIva,
        impuestoTasas,
      );

      return {
        codigoArticulo:
          tipo === 'G'
            ? String(it.nroCuenta ?? it.codigoArticulo?.trim() ?? '')
            : it.codigoArticulo?.trim() || '',
        descripcion: it.descripcion?.trim() || '',
        cantidad,
        costoBrutoUnitario,
        porcDesc1: 0,
        porcDesc2: 0,
        porcDesc3: 0,
        porcDesc4: 0,
        porcDesc5: 0,
        notas: tipo === 'G' ? it.notas?.trim() || '' : '',
        tipoProveedor: tipo,
        nroCuenta: tipo === 'G' ? it.nroCuenta ?? undefined : undefined,
        ...tasas,
      };
    });

export const LABELS_PRECARGA_CAMPO: Record<string, string> = {
  cuit: 'CUIT proveedor',
  letra: 'Letra del comprobante',
  tipoComprobante: 'Tipo comprobante AFIP',
  nroSuc: 'Punto de venta (sucursal)',
  nroComprobante: 'Nro. comprobante',
  fechaEmision: 'Fecha emisión',
  fechaVencim: 'Fecha vencimiento',
  esNotaDebito: 'Nota de débito',
  detalleLineas: 'Líneas de detalle',
  descripcion: 'Observaciones',
};

/** Campos de encabezado que siempre se muestran en el modal de precarga. */
export const CAMPOS_ENCABEZADO_PRECARGA = [
  'cuit',
  'letra',
  'nroSuc',
  'nroComprobante',
  'fechaEmision',
  'fechaVencim',
  'tipoComprobante',
] as const;

export const MSG_CAMPO_NO_DETECTADO =
  'No se detectó en el PDF. Deberá completarlo manualmente al aplicar la precarga.';

/** Indica si la precarga incluyó datos obtenidos vía OpenAI (fuente openai en campos). */
export function precargaUtilizoIa(precarga: FacturaCompraPrecargaDesdePdfResult | null): boolean {
  if (!precarga) return false;
  return (precarga.campos ?? []).some((c) => c.fuente?.toLowerCase() === 'openai');
}

export interface FilaEncabezadoPrecarga {
  nombre: string;
  label: string;
  valor: string | null;
  noDetectado: boolean;
}

export function buildFilasEncabezadoPrecarga(
  precarga: FacturaCompraPrecargaDesdePdfResult,
): FilaEncabezadoPrecarga[] {
  const campos = precarga.campos ?? [];
  const f = precarga.formulario ?? {};
  const valorCampo = (nombre: string) => campos.find((c) => c.nombre === nombre)?.valor?.trim() || null;

  const fila = (
    nombre: (typeof CAMPOS_ENCABEZADO_PRECARGA)[number],
    ...fallbacks: (string | number | null | undefined)[]
  ): FilaEncabezadoPrecarga => {
    const detectadoEnCampos = valorCampo(nombre);
    if (detectadoEnCampos) {
      return {
        nombre,
        label: LABELS_PRECARGA_CAMPO[nombre] ?? nombre,
        valor: detectadoEnCampos,
        noDetectado: false,
      };
    }

    for (const fb of fallbacks) {
      if (fb == null) continue;
      if (typeof fb === 'number') {
        if (fb > 0) {
          return {
            nombre,
            label: LABELS_PRECARGA_CAMPO[nombre] ?? nombre,
            valor: String(fb),
            noDetectado: false,
          };
        }
        continue;
      }
      const s = String(fb).trim();
      if (s) {
        return {
          nombre,
          label: LABELS_PRECARGA_CAMPO[nombre] ?? nombre,
          valor: s,
          noDetectado: false,
        };
      }
    }

    return {
      nombre,
      label: LABELS_PRECARGA_CAMPO[nombre] ?? nombre,
      valor: null,
      noDetectado: true,
    };
  };

  return [
    fila('cuit', precarga.proveedor?.cuit),
    fila('letra', f.letra),
    fila('nroSuc', f.nroSuc),
    fila('nroComprobante', f.nroComprobante),
    fila('fechaEmision', f.fechaEmision),
    fila('fechaVencim', f.fechaVencim),
    fila('tipoComprobante', f.tipoComprobante),
  ];
}

export function buildFilaObservacionesPrecarga(
  precarga: FacturaCompraPrecargaDesdePdfResult,
): FilaEncabezadoPrecarga {
  const observaciones = precarga.formulario?.descripcion?.trim() || '';
  return {
    nombre: 'descripcion',
    label: LABELS_PRECARGA_CAMPO.descripcion,
    valor: observaciones || null,
    noDetectado: !observaciones,
  };
}

export interface ProveedorInicialDesdePrecarga {
  nombre?: string;
  cuit?: string;
  domicilio?: string;
  tipoIva: string;
  tipoProveedor: 'C' | 'G';
}

/** Indica si conviene ofrecer dar de alta el proveedor con datos del PDF. */
export function debeSugerirCrearProveedor(
  precarga: FacturaCompraPrecargaDesdePdfResult | null,
): boolean {
  if (!precarga || precarga.proveedor.encontrado) return false;
  const nombre = precarga.proveedor.nombre?.trim();
  const cuit = precarga.proveedor.cuit?.trim();
  return Boolean(nombre || cuit);
}

/** Inferir tipo IVA del emisor según letra del comprobante leída. */
export function tipoIvaSugeridoDesdeLetra(letra?: string | null): string {
  const L = (letra ?? '').trim().toUpperCase();
  if (L === 'C') return 'M';
  if (L === 'B') return 'I';
  if (L === 'A') return 'I';
  return 'I';
}

/** C o G según formulario.tipoProveedor o predominio de ítems detectados. */
export function tipoProveedorSugeridoDesdePrecarga(
  precarga: FacturaCompraPrecargaDesdePdfResult,
): 'C' | 'G' {
  const tipoForm = precarga.formulario?.tipoProveedor?.trim().toUpperCase();
  if (tipoForm === 'G') return 'G';
  if (tipoForm === 'C') return 'C';

  const conceptos = precarga.itemsConceptos?.length ?? 0;
  const productos = precarga.itemsProductos?.length ?? 0;
  if (conceptos > 0 && productos === 0) return 'G';
  return 'C';
}

/** Datos iniciales para el diálogo de alta de proveedor desde precarga PDF. */
export function buildProveedorInicialDesdePrecarga(
  precarga: FacturaCompraPrecargaDesdePdfResult,
): ProveedorInicialDesdePrecarga {
  const letra =
    precarga.formulario?.letra?.trim() ||
    precarga.campos?.find((c) => c.nombre === 'letra')?.valor?.trim() ||
    null;

  return {
    nombre: precarga.proveedor.nombre?.trim() || undefined,
    cuit: precarga.proveedor.cuit?.trim() || undefined,
    domicilio: precarga.proveedor.domicilio?.trim() || undefined,
    tipoIva: tipoIvaSugeridoDesdeLetra(letra),
    tipoProveedor: tipoProveedorSugeridoDesdePrecarga(precarga),
  };
}

/** Corrige proveedor de la precarga usando datos del emisor (columna izquierda del PDF AFIP). */
export function corregirPrecargaProveedorConEmisor(
  result: FacturaCompraPrecargaDesdePdfResult,
  emisor: EmisorFacturaCompraPdf,
): { precarga: FacturaCompraPrecargaDesdePdfResult; corregido: boolean } {
  const cuitEmisor = emisor.cuit?.replace(/\D/g, '') ?? '';
  const cuitActual = (result.proveedor.cuit ?? '').replace(/\D/g, '');
  const nombreEmisor = emisor.nombre?.trim() ?? '';
  const nombreActual = result.proveedor.nombre?.trim() ?? '';

  const cambioCuit = Boolean(cuitEmisor && cuitEmisor !== cuitActual);
  const cambioNombre = Boolean(nombreEmisor && nombreEmisor !== nombreActual);
  const hayDomicilio = Boolean(emisor.domicilio?.trim());

  if (!cambioCuit && !cambioNombre && !hayDomicilio) {
    return { precarga: result, corregido: false };
  }

  const cuitFinal = cuitEmisor || cuitActual || undefined;
  const nombreFinal = nombreEmisor || nombreActual || undefined;
  const cuitDisplay = formatCuit11(cuitFinal) ?? cuitFinal ?? null;

  const campos = [...(result.campos ?? [])];
  const idxCuit = campos.findIndex((c) => c.nombre === 'cuit');
  if (idxCuit >= 0) {
    campos[idxCuit] = { ...campos[idxCuit], valor: cuitDisplay, fuente: 'pdf-emisor' };
  } else if (cuitDisplay) {
    campos.push({ nombre: 'cuit', valor: cuitDisplay, confianza: 0.95, fuente: 'pdf-emisor' });
  }

  const advertencias = [...(result.advertencias ?? [])];
  if (cambioCuit || cambioNombre) {
    advertencias.push(
      'Se utilizaron los datos del emisor del comprobante (columna izquierda) como proveedor.',
    );
  }

  const reiniciarMatch = cambioCuit || cambioNombre;

  return {
    corregido: true,
    precarga: {
      ...result,
      campos,
      advertencias,
      proveedor: {
        ...result.proveedor,
        nombre: nombreFinal || null,
        cuit: cuitDisplay,
        domicilio: emisor.domicilio?.trim() || result.proveedor.domicilio || null,
        encontrado: reiniciarMatch ? false : result.proveedor.encontrado,
        nroProveedor: reiniciarMatch ? null : result.proveedor.nroProveedor,
      },
    },
  };
}

/** Texto descriptivo del proveedor detectado o leído del PDF. */
export function labelProveedorPrecarga(precarga: FacturaCompraPrecargaDesdePdfResult): string {
  const nombre = precarga.proveedor.nombre?.trim();
  const cuit = precarga.proveedor.cuit?.trim();
  if (nombre && cuit) return `${nombre} (${cuit})`;
  return nombre || cuit || 'sin identificar';
}
